import app from 'flarum/forum/app';
import { extend, override } from 'flarum/common/extend';
import Post from 'flarum/forum/components/Post';
import humanTime from 'flarum/common/utils/humanTime';
import HandleAvatar from './HandleAvatar';
import { handleOf } from './handle';

/**
 * What moderators see of a flag -- Task 6.3a.
 *
 * The API no longer says who raised a flag (`HideFlaggerFromOthers`), and has
 * not named the flagged post's author since 5.5. flarum-flags still asks for
 * both, gets nothing, and falls back to core's `username()` helper, which
 * prints **"[deleted]"**. So a moderator read *"Flagged by [deleted]"* on a post
 * and *"[deleted] in Some Discussion"* in the flag dropdown. That is untrue, and
 * it looks like a bug somebody should fix by giving the names back, which is
 * the 5.7 `/u/` lesson.
 *
 *   - **Who flagged it** now reads *"someone"*, unless it was you. You are the
 *     one person the API still tells (your own flag points at you).
 *   - **Whose post it is** now reads as the post's handle, with its monogram,
 *     which is what every other surface shows for the same post.
 *
 * Nothing here decides what is sent; the API is the trust boundary. This only
 * stops the moderator's screen claiming that people have been deleted.
 *
 * BOTH PATCHES ARE ABSENT-SAFE. `flagReason` exists only once flarum-flags has
 * added it to `Post`, and the dropdown is patched by module path, which Flarum
 * applies when that module loads and never if flags is disabled. A static
 * import of a flags module would be `undefined` when flags is off and would
 * throw at boot, taking this whole extension down (the 4.2 trap).
 */
export default function flags() {
  flagReason();
  flagDropdown();
}
function flagReason() {
  const proto = Post.prototype;
  if (typeof proto.flagReason !== 'function') return;

  /*
   * The same shape as flags' own `flagReason` (addFlagsToPosts.js), with one
   * difference: the name. Flags passes `user`, and the translator turns an
   * absent user into "[deleted]" (Translator.tsx, `username()`). Passing
   * `username` directly skips that.
   */
  override(proto, 'flagReason', function (original, flag) {
    if (flag.type() !== 'user') return original(flag);
    const flagger = flag.user();
    const isYou = !!flagger && flagger === app.session.user;

    // The API sends the flagger only to the flagger themselves. Anything else
    // arriving here means that stopped being true, and the original is the
    // honest thing to show while it is investigated.
    if (flagger && !isYou) return original(flag);
    const reason = flag.reason() ? app.translator.trans(`flarum-flags.forum.flag_post.reason_${flag.reason()}_label`) : null;
    const detail = flag.reasonDetail();
    const name = m("span", {
      className: "username"
    }, app.translator.trans(isYou ? 'ideasynq-handles.forum.flags.you' : 'ideasynq-handles.forum.flags.someone'));
    return [app.translator.trans(reason ? 'flarum-flags.forum.post.flagged_by_with_reason_text' : 'flarum-flags.forum.post.flagged_by_text', {
      time: humanTime(flag.createdAt()),
      username: name,
      reason
    }), !!detail && m("span", {
      className: "Post-flagged-detail"
    }, detail)];
  });
}
function flagDropdown() {
  /*
   * `content()` returns one array of `<li>` per page, each wrapping a single
   * HeaderListItem, in the same order as the state's pages and items. Swap the
   * avatar and the text on each, pairing by position. If that shape ever
   * changes, nothing is touched: the fallback is flags' own rendering, not a
   * blank.
   */
  extend('ext:flarum/flags/forum/components/FlagList', 'content', function (vdom, state) {
    if (!Array.isArray(vdom) || !state?.getPages) return;
    const flagsInOrder = state.getPages().flatMap(page => page.items);
    const items = vdom.flat();
    if (items.length !== flagsInOrder.length) return;
    items.forEach((li, i) => {
      const itemVnode = Array.isArray(li?.children) ? li.children[0] : null;
      const post = flagsInOrder[i]?.post?.();
      const handle = handleOf(post);
      if (!itemVnode?.attrs || !post || !handle) return;
      itemVnode.attrs.avatar = HandleAvatar(handle);
      itemVnode.attrs.content = app.translator.trans('flarum-flags.forum.flagged_posts.item_text', {
        username: m("span", {
          className: "username"
        }, handle),
        em: m("em", null),
        discussion: post.discussion()?.title()
      });
    });
  });
}